import { useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

interface Location { id: number; name: string; }
interface Inventory { id: number; name: string; stock: number; selling_price: number; jenis: 'bahan_baku' | 'produk_jadi'; location_id: number | null; }
interface PaymentMethod { id: number; name: string; }
interface Customer { id: number; name: string; phone: string | null; }
interface SaleItem { inventory_id: number; quantity: number; price: number; }
interface Sale {
  id: number; date: string; customer_name: string; customer_id: number | null;
  location_id: number; location_name: string; username: string;
  total: number; notes: string;
  payment_method_id: number; payment_method_name: string;
  item_names: string; item_count: number;
}
interface SaleDetail {
  sale: Sale;
  items: Array<{ id: number; inventory_id: number; inventory_name: string; quantity: number; price: number; subtotal: number; }>;
}

const rupiahFormat = (n: number) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);

const escapeHtml = (value: any) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = { date: today(), customer_id: '', location_id: '', notes: '', payment_method_id: '' };
const emptyItem = (): SaleItem => ({ inventory_id: 0, quantity: 1, price: 0 });
const formatPhone = (phone: string) => {
  let p = phone.replace(/\D/g, '');
  if (p.startsWith('0')) p = '62' + p.slice(1);
  if (!p.startsWith('62')) p = '62' + p;
  return p;
};

export default function Penjualan() {
  const { role } = useAuth();
  const isAdmin = role === 'admin';

  const [sales, setSales] = useState<Sale[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [inventories, setInventories] = useState<Inventory[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPayment, setFilterPayment] = useState<string>('');
  const [filterCustomer, setFilterCustomer] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saleItems, setSaleItems] = useState<SaleItem[]>([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [detail, setDetail] = useState<SaleDetail | null>(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'list' | 'report'>('list');
  const [report, setReport] = useState<{ label: string; total_sales: number }[]>([]);
  const [reportType, setReportType] = useState<'daily' | 'monthly'>('daily');
  const [selectedSaleIds, setSelectedSaleIds] = useState<number[]>([]);
  const [printingCombined, setPrintingCombined] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    // Gunakan allSettled agar satu error tidak block yang lain
    const [saleRes, locRes, invRes, pmRes, custRes] = await Promise.allSettled([
      api.get('/sales/getAllSales'),
      api.get('/locations/getAllLocations'),
      api.get('/inventories/getAllInventories'),
      api.get('/payment-methods/getAll'),
      api.get('/customers/getAll'),
    ]);

    if (saleRes.status === 'fulfilled') setSales(saleRes.value.data);
    if (locRes.status === 'fulfilled') setLocations(locRes.value.data);
    if (invRes.status === 'fulfilled') setInventories(invRes.value.data);
    if (pmRes.status === 'fulfilled') setPaymentMethods(pmRes.value.data);
    if (custRes.status === 'fulfilled') setCustomers(custRes.value.data);

    setLoading(false);
  };

  const fetchReport = async (type: 'daily' | 'monthly') => {
    try {
      const res = await api.get(`/sales/report?type=${type}`);
      setReport(res.data.data || []);
    } catch {
      setError('Gagal memuat laporan');
    }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (activeTab === 'report') fetchReport(reportType); }, [activeTab, reportType]);

  const openAdd = () => {
    setEditId(null);
    setForm(emptyForm);
    setSaleItems([emptyItem()]);
    setError('');
    setShowModal(true);
  };

  const openEdit = async (id: number) => {
    try {
      const res = await api.get(`/sales/getSaleById/${id}`);
      const d: SaleDetail = res.data;
      setEditId(id);
      setForm({
        date: d.sale.date?.slice(0, 10) || today(),
        customer_id: d.sale.customer_id?.toString() || '',
        location_id: d.sale.location_id?.toString() || '',
        notes: d.sale.notes || '',
        payment_method_id: d.sale.payment_method_id?.toString() || '',
      });
      setSaleItems(d.items.map((i) => ({ inventory_id: i.inventory_id, quantity: i.quantity, price: i.price })));
      setError('');
      setShowModal(true);
    } catch {
      setError('Gagal memuat detail');
    }
  };

  const openDetail = async (id: number) => {
    try {
      const res = await api.get(`/sales/getSaleById/${id}`);
      setDetail(res.data);
    } catch {
      setError('Gagal memuat detail');
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.customer_id) { setError('Pilih pelanggan'); return; }
    if (saleItems.some((i) => !i.inventory_id)) { setError('Pilih produk untuk setiap baris'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        date: form.date,
        customer_id: Number(form.customer_id),
        location_id: Number(form.location_id),
        notes: form.notes,
        payment_method_id: form.payment_method_id ? Number(form.payment_method_id) : null,
        items: saleItems,
      };
      if (editId) {
        await api.put(`/sales/updateSale/${editId}`, payload);
      } else {
        await api.post('/sales/createSale', payload);
      }
      setShowModal(false);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/sales/deleteSale/${deleteId}`);
      setDeleteId(null);
      fetchData();
    } catch {
      setError('Gagal menghapus');
    }
  };

  const handlePrint = (d: SaleDetail) => {
    const tanggal = new Date(d.sale.date).toLocaleDateString('id-ID', { dateStyle: 'long' });
    const rows = d.items.map((item) => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #f0f0f0;">${item.inventory_name}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #f0f0f0;text-align:center;">${item.quantity}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #f0f0f0;text-align:right;">${rupiahFormat(item.price)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:600;">${rupiahFormat(item.subtotal)}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="id">
<head>
  <meta charset="UTF-8" />
  <title>Invoice #${d.sale.id}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 13px; color: #1a1a1a; background: #fff; padding: 32px; }
    .invoice-box { max-width: 680px; margin: auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; }
    .brand { font-size: 22px; font-weight: 700; color: #4338ca; letter-spacing: -0.5px; }
    .brand-sub { font-size: 11px; color: #888; margin-top: 2px; }
    .invoice-title { text-align: right; }
    .invoice-title h2 { font-size: 18px; font-weight: 700; color: #374151; }
    .invoice-title p { color: #9ca3af; font-size: 11px; margin-top: 2px; }
    .divider { border: none; border-top: 2px solid #e5e7eb; margin: 20px 0; }
    .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 32px; margin-bottom: 24px; }
    .meta-item label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #9ca3af; display: block; margin-bottom: 2px; }
    .meta-item span { font-weight: 600; color: #1f2937; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 0; }
    thead tr { background: #f3f4f6; }
    thead th { padding: 9px 8px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; font-weight: 600; }
    thead th:not(:first-child) { text-align: center; }
    thead th:last-child { text-align: right; }
    .total-row td { padding: 10px 8px; font-weight: 700; font-size: 14px; border-top: 2px solid #e5e7eb; }
    .total-row td:last-child { color: #16a34a; text-align: right; font-size: 15px; }
    .notes { margin-top: 20px; padding: 12px 14px; background: #f9fafb; border-radius: 8px; font-size: 12px; color: #6b7280; }
    .footer { margin-top: 32px; text-align: center; font-size: 11px; color: #d1d5db; border-top: 1px solid #f3f4f6; padding-top: 16px; }
    @media print { body { padding: 16px; } }
  </style>
</head>
<body>
  <div class="invoice-box">
    <div class="header">
      <div>
        <div class="brand">TemanSkrispy</div>
        <div class="brand-sub">Sistem Manajemen Toko</div>
      </div>
      <div class="invoice-title">
        <h2>INVOICE</h2>
        <p>#INV-${String(d.sale.id).padStart(5, '0')}</p>
      </div>
    </div>
    <hr class="divider" />
    <div class="meta-grid">
      <div class="meta-item"><label>Tanggal</label><span>${tanggal}</span></div>
      <div class="meta-item"><label>Lokasi</label><span>${d.sale.location_name || '-'}</span></div>
      <div class="meta-item"><label>Pelanggan</label><span>${d.sale.customer_name || '-'}</span></div>
      <div class="meta-item"><label>Kasir</label><span>${d.sale.username || '-'}</span></div>
      <div class="meta-item"><label>Metode Pembayaran</label><span>${d.sale.payment_method_name || '-'}</span></div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Produk</th>
          <th style="text-align:center;">Qty</th>
          <th style="text-align:right;">Harga Satuan</th>
          <th style="text-align:right;">Subtotal</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
      <tfoot>
        <tr class="total-row">
          <td colspan="3">Total</td>
          <td>${rupiahFormat(d.sale.total)}</td>
        </tr>
      </tfoot>
    </table>
    ${d.sale.notes ? `<div class="notes"><strong>Catatan:</strong> ${d.sale.notes}</div>` : ''}
    <div class="footer">Terima kasih atas kepercayaan Anda &bull; TemanSkrispy</div>
  </div>
  <script>window.onload = () => { window.print(); }</script>
</body>
</html>`;

    const win = window.open('', '_blank', 'width=780,height=900');
    if (win) { win.document.write(html); win.document.close(); }
  };

  const isUnpaidSale = (sale: Sale) => {
    const method = (sale.payment_method_name || '').trim().toLowerCase();
    return !sale.payment_method_id || !method || method === 'none';
  };

  const toggleSaleSelection = (sale: Sale) => {
    if (!isUnpaidSale(sale)) return;
    setSelectedSaleIds((prev) =>
      prev.includes(sale.id) ? prev.filter((id) => id !== sale.id) : [...prev, sale.id]
    );
  };

  const handlePrintCombined = async () => {
    if (selectedSaleIds.length === 0) return;

    const selectedSales = sales.filter((sale) => selectedSaleIds.includes(sale.id));
    const customerKeys = new Set(selectedSales.map((sale) => sale.customer_id || sale.customer_name || ''));
    if (customerKeys.size > 1) {
      setError('Pilih transaksi dari satu pelanggan yang sama');
      return;
    }

    setPrintingCombined(true);
    setError('');
    try {
      const details = await Promise.all(
        selectedSaleIds.map((id) => api.get(`/sales/getSaleById/${id}`).then((res) => res.data as SaleDetail))
      );

      const sortedDetails = details.sort((a, b) => new Date(a.sale.date).getTime() - new Date(b.sale.date).getTime());
      const customerName = sortedDetails[0]?.sale.customer_name || '-';
      const printDate = new Date().toLocaleDateString('id-ID', { dateStyle: 'long' });
      const fileCode = `INV-${String(sortedDetails[0]?.sale.id || 0).padStart(5, '0')}`;
      const totalBelanja = sortedDetails.reduce((acc, item) => acc + Number(item.sale.total || 0), 0);

      const invoiceSections = sortedDetails.map((detailItem) => {
        const tanggal = new Date(detailItem.sale.date).toLocaleDateString('id-ID', { dateStyle: 'long' });
        const rows = detailItem.items.map((item) => `
          <tr>
            <td style="padding:6px 8px;border-bottom:1px solid #f0f0f0;">${escapeHtml(item.inventory_name)}</td>
            <td style="padding:6px 8px;border-bottom:1px solid #f0f0f0;text-align:center;">${item.quantity}</td>
            <td style="padding:6px 8px;border-bottom:1px solid #f0f0f0;text-align:right;">${rupiahFormat(item.price)}</td>
            <td style="padding:6px 8px;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:600;">${rupiahFormat(item.subtotal)}</td>
          </tr>`).join('');

        return `
          <section class="invoice-section">
            <div class="section-head">
              <div>
                <h3>Invoice #INV-${String(detailItem.sale.id).padStart(5, '0')}</h3>
                <p>${tanggal} &bull; ${escapeHtml(detailItem.sale.location_name || '-')}</p>
              </div>
              <div class="subtotal">${rupiahFormat(detailItem.sale.total)}</div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Produk</th>
                  <th style="text-align:center;">Qty</th>
                  <th style="text-align:right;">Harga Satuan</th>
                  <th style="text-align:right;">Subtotal</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
          </section>`;
      }).join('');

      const html = `<!DOCTYPE html>
<html lang="id">
<head>
  <meta charset="UTF-8" />
  <title>${fileCode}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 13px; color: #1a1a1a; background: #fff; padding: 32px; }
    .invoice-box { max-width: 720px; margin: auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; }
    .brand { font-size: 22px; font-weight: 700; color: #4338ca; letter-spacing: -0.5px; }
    .brand-sub { font-size: 11px; color: #888; margin-top: 2px; }
    .invoice-title { text-align: right; }
    .invoice-title h2 { font-size: 18px; font-weight: 700; color: #374151; }
    .invoice-title p { color: #9ca3af; font-size: 11px; margin-top: 2px; }
    .divider { border: none; border-top: 2px solid #e5e7eb; margin: 20px 0; }
    .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 32px; margin-bottom: 24px; }
    .meta-item label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #9ca3af; display: block; margin-bottom: 2px; }
    .meta-item span { font-weight: 600; color: #1f2937; }
    .invoice-section { margin-top: 18px; border: 1px solid #f3f4f6; border-radius: 10px; overflow: hidden; }
    .section-head { display: flex; justify-content: space-between; gap: 16px; align-items: flex-start; background: #f9fafb; padding: 12px 14px; border-bottom: 1px solid #f3f4f6; }
    .section-head h3 { font-size: 14px; color: #374151; }
    .section-head p { font-size: 11px; color: #9ca3af; margin-top: 2px; }
    .subtotal { font-size: 14px; font-weight: 700; color: #16a34a; white-space: nowrap; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 0; }
    thead tr { background: #fff; }
    thead th { padding: 9px 8px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #6b7280; font-weight: 600; }
    .notes { margin: 10px 12px 12px; padding: 10px 12px; background: #f9fafb; border-radius: 8px; font-size: 12px; color: #6b7280; }
    .grand-total { margin-top: 22px; padding: 14px 16px; background: #ecfdf5; border: 1px solid #bbf7d0; border-radius: 10px; display: flex; justify-content: space-between; align-items: center; }
    .grand-total span:first-child { font-size: 15px; font-weight: 700; color: #14532d; }
    .grand-total span:last-child { font-size: 19px; font-weight: 800; color: #16a34a; }
    .payment-box { margin-top: 12px; padding: 14px 16px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 10px; }
    .payment-box h3 { font-size: 13px; color: #374151; margin-bottom: 10px; display: flex; align-items: center; gap: 8px; }
    .payment-icon { width: 24px; height: 24px; border-radius: 999px; background: #4338ca; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; }
    .payment-list { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
    .bank-card { border: 1px solid #e5e7eb; background: #fff; border-radius: 10px; padding: 12px; }
    .bank-head { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
    .bank-badge { min-width: 42px; height: 24px; border-radius: 6px; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 800; letter-spacing: 0.04em; }
    .bank-bri { background: #00529c; }
    .bank-bca { background: #0066ae; }
    .bank-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #9ca3af; }
    .bank-number { font-size: 15px; color: #111827; font-weight: 800; letter-spacing: 0.02em; }
    .bank-name { margin-top: 4px; font-size: 11px; color: #6b7280; }
    @media print { .payment-list { grid-template-columns: 1fr 1fr; } }
    .footer { margin-top: 32px; text-align: center; font-size: 11px; color: #d1d5db; border-top: 1px solid #f3f4f6; padding-top: 16px; }
    @media print { body { padding: 16px; } .invoice-section { break-inside: avoid; } }
  </style>
</head>
<body>
  <div class="invoice-box">
    <div class="header">
      <div>
        <div class="brand">TemanSkrispy</div>
        <div class="brand-sub">Sistem Manajemen Toko</div>
      </div>
      <div class="invoice-title">
        <h2>RINCIAN BELANJA</h2>
        <p>${printDate}</p>
      </div>
    </div>
    <hr class="divider" />
    <div class="meta-grid">
      <div class="meta-item"><label>Pelanggan</label><span>${escapeHtml(customerName)}</span></div>
      <div class="meta-item"><label>Jumlah Invoice</label><span>${sortedDetails.length} invoice</span></div>
      <div class="meta-item"><label>Metode Pembayaran</label><span>None</span></div>
      <div class="meta-item"><label>Tanggal Cetak</label><span>${printDate}</span></div>
    </div>
    ${invoiceSections}
    <div class="grand-total">
      <span>Total Belanja</span>
      <span>${rupiahFormat(totalBelanja)}</span>
    </div>
    <div class="payment-box">
      <h3><span class="payment-icon">Rp</span> Pembayaran Transfer</h3>
      <div class="payment-list">
        <div class="bank-card">
          <div class="bank-head">
            <span class="bank-badge bank-bri">BRI</span>
            <span class="bank-label">Bank Rakyat Indonesia</span>
          </div>
          <div class="bank-number">0416 0103 0296 503</div>
          <div class="bank-name">a.n. Bogiant Raskarowana</div>
        </div>
        <div class="bank-card">
          <div class="bank-head">
            <span class="bank-badge bank-bca">BCA</span>
            <span class="bank-label">Bank Central Asia</span>
          </div>
          <div class="bank-number">5725779388</div>
          <div class="bank-name">a.n. Bogiant Raskarowana</div>
        </div>
      </div>
    </div>
    <div class="footer">Terima kasih atas kepercayaan Anda &bull; TemanSkrispy</div>
  </div>
  <script>window.onload = () => { window.print(); }</script>
</body>
</html>`;

      const win = window.open('', '_blank', 'width=820,height=900');
      if (win) { win.document.write(html); win.document.close(); }
    } catch {
      setError('Gagal mencetak belanja terpilih');
    } finally {
      setPrintingCombined(false);
    }
  };

  const handleWhatsAppReminder = async () => {
    if (selectedSaleIds.length === 0) return;

    const selectedSales = sales.filter((sale) => selectedSaleIds.includes(sale.id));
    const customerKeys = new Set(selectedSales.map((sale) => sale.customer_id || sale.customer_name || ''));
    if (customerKeys.size > 1) {
      setError('Pilih transaksi dari satu pelanggan yang sama');
      return;
    }

    const customerName = selectedSales[0]?.customer_name || '-';
    const customerId = selectedSales[0]?.customer_id;
    const customerPhone = customers.find((customer) => customer.id === customerId)?.phone;
    if (!customerPhone) {
      setError('Nomor WhatsApp customer belum tersedia di data pelanggan');
      return;
    }

    setSendingReminder(true);
    setError('');
    try {
      const details = await Promise.all(
        selectedSaleIds.map((id) => api.get(`/sales/getSaleById/${id}`).then((res) => res.data as SaleDetail))
      );
      const sortedDetails = details.sort((a, b) => new Date(a.sale.date).getTime() - new Date(b.sale.date).getTime());
      const totalBelanja = sortedDetails.reduce((acc, item) => acc + Number(item.sale.total || 0), 0);

      const itemLines = sortedDetails.flatMap((detailItem) =>
        detailItem.items.map((item) => `- ${item.inventory_name} x${item.quantity} = ${rupiahFormat(item.subtotal)}`)
      ).join('\n');

      const message = [
        `Halo ${customerName}, berikut rincian belanja yang belum dibayar:`,
        '',
        itemLines,
        '',
        `Total Belanja: ${rupiahFormat(totalBelanja)}`,
        '',
        'Pembayaran transfer:',
        'BRI 0416 0103 0296 503',
        'a.n. Bogiant Raskarowana',
        '',
        'BCA 5725779388',
        'a.n. Bogiant Raskarowana',
        '',
        'Terima kasih.',
      ].join('\n');

      window.open(`https://wa.me/${formatPhone(customerPhone)}?text=${encodeURIComponent(message)}`, '_blank');
    } catch {
      setError('Gagal membuat pesan WhatsApp');
    } finally {
      setSendingReminder(false);
    }
  };

  const updateItem = (index: number, field: keyof SaleItem, value: number) => {
    const updated = [...saleItems];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'inventory_id') {
      const inv = inventories.find((i) => i.id === value);
      if (inv) updated[index].price = inv.selling_price;
    }
    setSaleItems(updated);
  };

  // Produk yang tersedia di lokasi yang dipilih (produk_jadi, stok > 0)
  const availableInventories = inventories.filter(
    (inv) => inv.jenis === 'produk_jadi' &&
      inv.stock > 0 &&
      (form.location_id ? inv.location_id === Number(form.location_id) : true)
  );

  const totalForm = saleItems.reduce((acc, i) => acc + i.quantity * i.price, 0);
  const filtered = sales.filter((s) => {
    const matchSearch = (s.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
      s.id?.toString().includes(search) ||
      (s.item_names || '').toLowerCase().includes(search.toLowerCase());
    const matchPayment = !filterPayment || String(s.payment_method_id) === filterPayment;
    const matchCustomer = !filterCustomer || String(s.customer_id) === filterCustomer;
    return matchSearch && matchPayment && matchCustomer;
  });
  const selectedSales = sales.filter((sale) => selectedSaleIds.includes(sale.id));
  const selectedTotal = selectedSales.reduce((acc, sale) => acc + Number(sale.total || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Penjualan</h1>
          <p className="text-gray-500 text-sm mt-1">{sales.length} transaksi</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 transition">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Tambah Penjualan
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {(['list', 'report'] as const).map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${activeTab === tab ? 'bg-white shadow text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab === 'list' ? 'Daftar Transaksi' : 'Laporan'}
          </button>
        ))}
      </div>

      {activeTab === 'list' ? (
        <>
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Filter customer */}
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <select value={filterCustomer} onChange={(e) => setFilterCustomer(e.target.value)}
                className="pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white appearance-none cursor-pointer">
                <option value="">Semua Customer</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Filter metode bayar */}
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <select value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)}
                className="pl-9 pr-8 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white appearance-none cursor-pointer">
                <option value="">Semua Metode</option>
                {paymentMethods.map((pm) => (
                  <option key={pm.id} value={pm.id}>{pm.name}</option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input type="text" placeholder="Cari produk atau ID..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>

          {/* Active filter indicator */}
          {(filterCustomer || filterPayment || search) && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-400">Filter aktif:</span>
              {filterCustomer && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  {customers.find((c) => String(c.id) === filterCustomer)?.name}
                  <button onClick={() => setFilterCustomer('')} className="ml-0.5 hover:text-purple-900">×</button>
                </span>
              )}
              {filterPayment && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  {paymentMethods.find((pm) => String(pm.id) === filterPayment)?.name}
                  <button onClick={() => setFilterPayment('')} className="ml-0.5 hover:text-blue-900">×</button>
                </span>
              )}
              {search && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  "{search}"
                  <button onClick={() => setSearch('')} className="ml-0.5 hover:text-gray-900">×</button>
                </span>
              )}
              <button onClick={() => { setFilterCustomer(''); setFilterPayment(''); setSearch(''); }}
                className="text-xs text-red-500 hover:text-red-700 font-medium">
                Reset semua
              </button>
            </div>
          )}

          {selectedSaleIds.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-emerald-800">
                  {selectedSaleIds.length} transaksi dipilih
                </p>
                <p className="text-xs text-emerald-600 mt-0.5">
                  Total Belanja: {rupiahFormat(selectedTotal)}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setSelectedSaleIds([])}
                  className="px-3 py-2 text-xs font-medium text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition">
                  Batal Pilih
                </button>
                <button onClick={handleWhatsAppReminder} disabled={sendingReminder}
                  className="px-3 py-2 text-xs font-medium text-emerald-700 bg-white border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-60 transition">
                  {sendingReminder ? 'Menyiapkan...' : 'Chat WA'}
                </button>
                <button onClick={handlePrintCombined} disabled={printingCombined}
                  className="px-3 py-2 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-60 transition">
                  {printingCombined ? 'Menyiapkan...' : 'Cetak Belanja Terpilih'}
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-48 text-gray-400">
                <svg className="animate-spin w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Memuat...
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Pilih</th>
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">ID</th>
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tanggal</th>
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Pelanggan</th>
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Produk</th>
                      <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Metode Bayar</th>
                      <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Total</th>
                      <th className="text-center px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filtered.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-12 text-gray-400">Belum ada transaksi</td></tr>
                    ) : filtered.map((sale) => (
                      <tr key={sale.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-4">
                          <input
                            type="checkbox"
                            checked={selectedSaleIds.includes(sale.id)}
                            disabled={!isUnpaidSale(sale)}
                            onChange={() => toggleSaleSelection(sale)}
                            title={isUnpaidSale(sale) ? 'Pilih untuk cetak belanja' : 'Hanya transaksi metode None yang bisa dipilih'}
                            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 disabled:opacity-30"
                          />
                        </td>
                        <td className="px-5 py-4 text-gray-400 text-xs">#{sale.id}</td>
                        <td className="px-5 py-4 text-gray-600">{new Date(sale.date).toLocaleDateString('id-ID')}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0">
                              {(sale.customer_name || '?').charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-gray-800">{sale.customer_name || '-'}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 max-w-xs">
                          {sale.item_names ? (
                            <div>
                              <p className="text-gray-700 text-xs truncate" title={sale.item_names}>{sale.item_names}</p>
                              <p className="text-gray-400 text-xs mt-0.5">{sale.item_count} item</p>
                            </div>
                          ) : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="px-5 py-4">
                          {sale.payment_method_name ? (
                            <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                              {sale.payment_method_name}
                            </span>
                          ) : <span className="text-gray-400 text-xs">-</span>}
                        </td>
                        <td className="px-5 py-4 text-right font-semibold text-green-600">{rupiahFormat(sale.total)}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button onClick={() => openDetail(sale.id)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition" title="Detail">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            </button>
                            {isAdmin && (
                              <>
                                <button onClick={() => openEdit(sale.id)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition" title="Edit">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                </button>
                                <button onClick={() => setDeleteId(sale.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition" title="Hapus">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-2">
            {(['daily', 'monthly'] as const).map((t) => (
              <button key={t} onClick={() => setReportType(t)}
                className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${reportType === t ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                {t === 'daily' ? 'Harian' : 'Bulanan'}
              </button>
            ))}
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">Laporan Penjualan {reportType === 'daily' ? 'Harian' : 'Bulanan'}</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {reportType === 'daily' ? 'Tanggal' : 'Bulan'}
                  </th>
                  <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Penjualan</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {report.length === 0 ? (
                  <tr><td colSpan={2} className="text-center py-12 text-gray-400">Belum ada data</td></tr>
                ) : report.map((r) => (
                  <tr key={r.label} className="hover:bg-gray-50">
                    <td className="px-5 py-4 text-gray-600">{r.label}</td>
                    <td className="px-5 py-4 text-right font-semibold text-green-600">{rupiahFormat(r.total_sales)}</td>
                  </tr>
                ))}
              </tbody>
              {report.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 border-t border-gray-200">
                    <td className="px-5 py-3.5 font-semibold text-gray-700">Total</td>
                    <td className="px-5 py-3.5 text-right font-bold text-green-700">
                      {rupiahFormat(report.reduce((acc, r) => acc + r.total_sales, 0))}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-lg font-semibold text-gray-800">{editId ? 'Edit Penjualan' : 'Tambah Penjualan'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-5">
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tanggal *</label>
                  <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Lokasi *</label>
                  <select required value={form.location_id} onChange={(e) => {
                    setForm({ ...form, location_id: e.target.value });
                    setSaleItems([emptyItem()]); // reset item saat lokasi berubah
                  }}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">-- Pilih Lokasi --</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Pelanggan *</label>
                  <select required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">-- Pilih Pelanggan --</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.phone ? ` (${c.phone})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Metode Pembayaran *</label>
                  <select required value={form.payment_method_id} onChange={(e) => setForm({ ...form, payment_method_id: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">-- Pilih Metode --</option>
                    {paymentMethods.map((pm) => <option key={pm.id} value={pm.id}>{pm.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Catatan</label>
                  <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Catatan (opsional)" />
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Item Produk</label>
                  <button type="button" onClick={() => setSaleItems([...saleItems, emptyItem()])}
                    className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Tambah Baris
                  </button>
                </div>
                <div className="space-y-2">
                  {saleItems.map((item, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <div className="flex-1">
                        <select value={item.inventory_id} onChange={(e) => updateItem(i, 'inventory_id', Number(e.target.value))}
                          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          disabled={!form.location_id}>
                          <option value={0}>{form.location_id ? '-- Pilih Produk --' : '-- Pilih lokasi dulu --'}</option>
                          {availableInventories.map((inv) => (
                            <option key={inv.id} value={inv.id}>{inv.name} (stok: {inv.stock})</option>
                          ))}
                        </select>
                        {form.location_id && availableInventories.length === 0 && (
                          <p className="text-xs text-amber-600 mt-1 pl-1">Tidak ada produk tersedia di lokasi ini</p>
                        )}
                      </div>
                      <input type="number" min={1} value={item.quantity} onChange={(e) => updateItem(i, 'quantity', Number(e.target.value))}
                        className="w-20 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Qty" />
                      <input type="number" min={0} value={item.price} onChange={(e) => updateItem(i, 'price', Number(e.target.value))}
                        className="w-32 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Harga" />
                      <span className="text-xs text-gray-500 w-28 text-right">{rupiahFormat(item.quantity * item.price)}</span>
                      {saleItems.length > 1 && (
                        <button type="button" onClick={() => setSaleItems(saleItems.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-3 pt-3 border-t border-gray-100">
                  <span className="text-sm font-semibold text-gray-800">Total: {rupiahFormat(totalForm)}</span>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">Batal</button>
                <button type="submit" disabled={saving} className="flex-1 bg-indigo-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition">
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-lg font-semibold text-gray-800">Detail Penjualan #{detail.sale.id}</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePrint(detail)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg text-sm font-medium transition"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Cetak Invoice
                </button>
                <button onClick={() => setDetail(null)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {[
                  { label: 'Tanggal', value: new Date(detail.sale.date).toLocaleDateString('id-ID', { dateStyle: 'long' }) },
                  { label: 'Pelanggan', value: detail.sale.customer_name || '-' },
                  { label: 'Lokasi', value: detail.sale.location_name || '-' },
                  { label: 'Kasir', value: detail.sale.username || '-' },
                  { label: 'Metode Bayar', value: detail.sale.payment_method_name || '-' },
                  { label: 'Catatan', value: detail.sale.notes || '-' },
                ].map((row) => (
                  <div key={row.label} className={row.label === 'Catatan' ? 'col-span-2' : ''}>
                    <p className="text-xs text-gray-400 mb-0.5">{row.label}</p>
                    <p className="font-medium text-gray-800">{row.value}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Item Produk</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-gray-400">
                      <th className="text-left pb-2">Produk</th>
                      <th className="text-right pb-2">Qty</th>
                      <th className="text-right pb-2">Harga</th>
                      <th className="text-right pb-2">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {detail.items.map((item) => (
                      <tr key={item.id}>
                        <td className="py-2 text-gray-700">{item.inventory_name}</td>
                        <td className="py-2 text-right text-gray-600">{item.quantity}</td>
                        <td className="py-2 text-right text-gray-600">{rupiahFormat(item.price)}</td>
                        <td className="py-2 text-right font-medium">{rupiahFormat(item.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-gray-200">
                      <td colSpan={3} className="pt-3 font-semibold text-gray-700">Total</td>
                      <td className="pt-3 text-right font-bold text-green-600">{rupiahFormat(detail.sale.total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Hapus Penjualan?</h3>
            <p className="text-gray-500 text-sm mb-6">Stok produk akan dikembalikan.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 border border-gray-200 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition">Batal</button>
              <button onClick={handleDelete} className="flex-1 bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-red-600 transition">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
